import Vue from "vue";
import Vuex from "vuex";
import i18n from "@/i18n/i18n";
import createPersistedState from "vuex-persistedstate";
import {
  BASIC_PLAN,
  LITE_PLAN,
  PRO_PLAN,
  PREMIUM_PLAN,
  TRIAL_PLAN,
} from "../constants";
import configModule from "./modules/config";
import languages from "./modules/languages";
import customReport from "./modules/customReport";
import orgUsers from "./modules/orgUsers";
import students from "./modules/students";
import { resetLocalStorageSavedFormData } from "@/utils/userUtils";

Vue.use(Vuex);

export default new Vuex.Store({
  strict: true,
  plugins: [
    createPersistedState({
      paths: ["user", "featureFlags", "customReport"],
    }),
  ],
  modules: {
    config: configModule,
    languages,
    customReport,
    orgUsers,
    students,
  },
  state: {
    user: null,
    featureFlags: {},
    aiDetectorEssayContent: "",
    clerkOrgContext: {
      orgId: null,
      clerkUserId: null,
      userId: null,
      isOrgAdmin: false,
    },
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    logout(state) {
      state.user = null;
      state.clerkOrgContext = {
        orgId: null,
        clerkUserId: null,
        userId: null,
        isOrgAdmin: false,
      };
    },
    setFeatureFlags(state, flags = {}) {
      state.featureFlags = flags;
    },
    setAiDetectorEssayContent(state, essayContent = "") {
      state.aiDetectorEssayContent = essayContent;
    },
    setClerkOrgContext(state, context) {
      state.clerkOrgContext = context;
    },
  },
  actions: {
    setUser({ commit }, user) {
      commit("setUser", user);
    },
    logout({ commit }) {
      resetLocalStorageSavedFormData();
      commit("logout");
      commit("customReport/clear");
    },
    setFeatureFlags({ commit }, flags) {
      commit("setFeatureFlags", flags);
    },
    setAiDetectorEssayContent({ commit }, essayContent) {
      commit("setAiDetectorEssayContent", essayContent);
    },
    setClerkOrgContext({ commit }, context) {
      commit("setClerkOrgContext", context);
    },
  },
  getters: {
    isPaused(state) {
      return state.user?.isPaused;
    },
    pausedStorageKey(state) {
      return `${state?.user?.id}-hidePausedAlert`;
    },
    userIsNotPausedAndPlanMoreThenPro(state, getters) {
      return (
        !getters.isPaused &&
        (getters.isTrial || getters.proPlan || getters.premiumPlan)
      );
    },
    trialPlan(state) {
      return state.user?.plan === TRIAL_PLAN;
    },
    basicPlan(state) {
      return state.user?.plan === BASIC_PLAN;
    },
    litePlan(state) {
      return state.user?.plan === LITE_PLAN;
    },
    proPlan(state) {
      return state.user?.plan === PRO_PLAN;
    },
    premiumPlan(state) {
      return state.user?.plan === PREMIUM_PLAN;
    },
    isGoogleConnected(state) {
      return state.user?.hasGoogle;
    },
    isCanvasConnected(state) {
      return state.user?.hasCanvas;
    },
    isSchoologyConnected(state) {
      return state.user?.hasSchoology;
    },
    manualCustomer(state) {
      return (
        state.user?.plan !== BASIC_PLAN && state.user?.stripeCustomerId === null
      );
    },
    isTrial(state) {
      return state.user?.plan === TRIAL_PLAN;
    },
    isTrialExpired(state, getters) {
      return (
        state.user?.trialEndsIn !== null &&
        !state.user?.trialEndsIn &&
        getters.basicPlan
      ); // 0 when expired
    },
    isTrialUntil(state) {
      return !state.user?.trialUntil; // 0 when expired
    },
    trialEndsInDays(state) {
      return Math.floor(Math.round(state.user.trialEndsIn / (24 * 60 * 60)));
    },
    trialProgress(state) {
      return Number(
        (state.user.trialEndsIn / state.user.trialSpan) * 100
      ).toFixed(2);
    },
    trialStartFeaturesList() {
      const features = i18n.t("dashboard.trial.startFeatures");
      if (Array.isArray(features)) {
        return features;
      }
      if (typeof features === "string") {
        return features.split(",").map((feature) => feature.trim());
      }
      return [];
    },
    trialEndFeaturesList() {
      const features = i18n.t("dashboard.trial.endFeatures");
      if (Array.isArray(features)) {
        return features;
      }
      if (typeof features === "string") {
        return features.split(",").map((feature) => feature.trim());
      }
      return [];
    },
    orgId(state) {
      return state.clerkOrgContext.orgId;
    },
    clerkUserId(state) {
      return state.clerkOrgContext.clerkUserId;
    },
    userId(state) {
      return state.clerkOrgContext.userId;
    },
    isOrgAdmin(state) {
      return state.clerkOrgContext.isOrgAdmin;
    },
  },
});
